import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, GraduationCap, Sparkles, Briefcase, MapPin, BadgeCheck, PlayCircle, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";

/**
 * /learn-earn — Slice K · Work → Learn → Earn.
 *
 * Two-column layout:
 *   • Left: completed vocational lessons (`/api/vle/completions`)
 *   • Right: matched local job listings for those categories (`/api/vle/gigs`)
 *
 * The mapping is done backend-side using a small alias table + city filter.
 */
export default function LearnEarn() {
  const [comps, setComps] = useState(null);
  const [gigs, setGigs]   = useState(null);
  const [cat, setCat]     = useState("");   // active category filter
  const [city, setCity]   = useState("");
  const [catalog, setCatalog] = useState(null);
  const [busyLesson, setBusyLesson] = useState(null);
  useSeo({
    title: "Learn & Earn — Find2Near",
    description: "Finish a vocational lesson and see jobs hiring for that skill in your city.",
  });

  const load = async (filterCat, filterCity) => {
    try {
      const [c, g, cat] = await Promise.all([
        api.get("/vle/completions"),
        api.get("/vle/gigs", { params: { category: filterCat || undefined, city: filterCity || undefined } }),
        api.get("/vle/catalog", { params: { category: filterCat || undefined } }),
      ]);
      setComps(c.data);
      setGigs(g.data);
      setCatalog(cat.data);
    } catch {
      toast.error("Please sign in to see your Learn-Earn dashboard");
    }
  };
  useEffect(() => { load("", ""); }, []);

  const applyCat = (c) => {
    setCat(c === cat ? "" : c);
    load(c === cat ? "" : c, city);
  };

  const completeLesson = async (lesson) => {
    setBusyLesson(lesson.lesson_id);
    try {
      const r = await api.post("/vle/complete", {
        lesson_id: lesson.lesson_id,
        category:  lesson.category,
        score:     85,
      });
      toast.success(`+${r.data.xp_earned} XP · lesson completed`);
      load(cat, city);
    } catch {
      toast.error("Couldn't record completion");
    } finally {
      setBusyLesson(null);
    }
  };

  const categories = (comps?.items || []).reduce((acc, r) => {
    if (r.category && !acc.includes(r.category)) acc.push(r.category);
    return acc;
  }, []);

  return (
    <div className="min-h-screen bg-[#F7FAF8] pb-16" data-testid="learn-earn-page">
      <div className="bg-gradient-to-b from-emerald-600 to-emerald-700 text-white px-4 pt-5 pb-8">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <Link to="/" className="p-2 -ml-2 rounded-full hover:bg-white/10" data-testid="learn-back">
            <ArrowLeft size={20} />
          </Link>
          <h1 className="heading font-black text-2xl">Learn & Earn</h1>
        </div>
        <div className="max-w-3xl mx-auto mt-3 text-sm text-emerald-50">
          Finish a lesson on the right skill — we&apos;ll show you jobs hiring for it in your city.
        </div>
        {comps && (
          <div className="max-w-3xl mx-auto mt-4 flex items-center gap-6 text-sm">
            <div className="flex items-center gap-1" data-testid="learn-xp">
              <Sparkles size={14} /> <b>{comps.xp || 0}</b> XP
            </div>
            <div className="flex items-center gap-1">
              <BadgeCheck size={14} /> <b>{comps.count || 0}</b> lesson{comps.count === 1 ? "" : "s"} completed
            </div>
          </div>
        )}
      </div>

      <div className="max-w-3xl mx-auto -mt-6 px-4">
        {/* City filter */}
        <div className="rounded-2xl bg-white shadow-sm border border-stone-100 p-3 flex items-center gap-2">
          <MapPin size={16} className="text-stone-500 shrink-0" />
          <input
            type="text"
            value={city}
            onChange={e => setCity(e.target.value)}
            onBlur={() => load(cat, city)}
            onKeyDown={e => { if (e.key === "Enter") load(cat, city); }}
            placeholder="City for job matches (e.g. Mumbai)"
            className="flex-1 text-sm outline-none"
            data-testid="learn-city"
          />
        </div>

        {/* Category chips */}
        {categories.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="learn-cat-chips">
            {categories.map(c => (
              <button
                key={c}
                onClick={() => applyCat(c)}
                className={`text-xs font-bold px-3 py-1.5 rounded-full border ${
                  c === cat
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"
                }`}
                data-testid={`learn-cat-${c}`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4 mt-4">
          {/* Completed lessons */}
          <div className="rounded-2xl bg-white border border-stone-100 p-4">
            <div className="text-xs font-bold uppercase text-stone-500 mb-2 flex items-center gap-1.5">
              <GraduationCap size={14} /> My completed lessons
            </div>
            {!comps && <div className="text-sm text-stone-400 py-4 text-center">Loading…</div>}
            {comps && (comps.items || []).length === 0 && (
              <div className="text-sm text-stone-500 bg-stone-50 rounded-xl p-4 text-center" data-testid="learn-comp-empty">
                No completed lessons yet. Try a starter lesson below.
              </div>
            )}
            <div className="space-y-2">
              {(comps?.items || []).map(l => (
                <div key={l.lesson_id} className="rounded-xl border border-stone-100 p-3" data-testid={`learn-comp-${l.lesson_id}`}>
                  <div className="text-sm font-bold text-stone-900 capitalize">{l.category}</div>
                  <div className="text-[11px] text-stone-500 truncate">
                    {l.lesson_id} · {l.score != null ? `${l.score}%` : "completed"}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Matched gigs */}
          <div className="rounded-2xl bg-white border border-stone-100 p-4">
            <div className="text-xs font-bold uppercase text-stone-500 mb-2 flex items-center gap-1.5">
              <Briefcase size={14} /> Jobs matching your skills
            </div>
            {!gigs && <div className="text-sm text-stone-400 py-4 text-center">Loading…</div>}
            {gigs?.reason === "no_completions" && (
              <div className="text-sm text-stone-500 bg-amber-50 border border-amber-100 rounded-xl p-4 text-center" data-testid="learn-gigs-empty">
                Complete at least one lesson to see matched jobs here.
              </div>
            )}
            {gigs && (gigs.items || []).length === 0 && gigs.reason !== "no_completions" && (
              <div className="text-sm text-stone-500 bg-stone-50 rounded-xl p-4 text-center" data-testid="learn-gigs-none">
                No live openings right now. Check back later or try a different city.
              </div>
            )}
            <div className="space-y-2">
              {(gigs?.items || []).map(j => (
                <Link
                  key={j.listing_id}
                  to={`/listing/${j.listing_id}`}
                  className="block rounded-xl border border-stone-100 p-3 hover:bg-stone-50"
                  data-testid={`learn-gig-${j.listing_id}`}
                >
                  <div className="text-sm font-bold text-stone-900 truncate">{j.title || j.subcategory || "Job opening"}</div>
                  <div className="text-[11px] text-stone-500 truncate mt-0.5">
                    {j.city && <>📍 {j.city} · </>}{j.subcategory}
                  </div>
                  {j.price != null && <div className="text-xs font-bold text-emerald-700 mt-1">₹{j.price}</div>}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Starter lesson catalog */}
        <div className="mt-4 rounded-2xl bg-white border border-stone-100 p-4" data-testid="learn-catalog">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-bold uppercase text-stone-500 flex items-center gap-1.5">
              <PlayCircle size={14} /> Starter lessons
            </div>
            <div className="text-[11px] text-stone-500">Tap to complete · earns instant XP</div>
          </div>
          {!catalog && <div className="text-sm text-stone-400 py-4 text-center">Loading…</div>}
          <div className="grid sm:grid-cols-2 gap-2">
            {(catalog?.items || []).map(l => (
              <div
                key={l.lesson_id}
                className={`rounded-xl border p-3 ${l.completed ? "border-emerald-200 bg-emerald-50" : "border-stone-100"}`}
                data-testid={`learn-lesson-${l.lesson_id}`}
              >
                <div className="text-[11px] font-bold uppercase text-stone-500 capitalize">{l.category}</div>
                <div className="text-sm font-bold text-stone-900 mt-0.5">{l.title}</div>
                {l.summary && <div className="text-[11px] text-stone-500 mt-1 line-clamp-2">{l.summary}</div>}
                <div className="mt-2 flex items-center justify-between">
                  <div className="text-[10px] text-stone-400">
                    {l.duration_min}m · {l.difficulty}
                  </div>
                  {l.completed
                    ? <span className="text-[11px] font-bold text-emerald-700 inline-flex items-center gap-1"><Check size={11} /> Completed</span>
                    : <button
                        onClick={() => completeLesson(l)}
                        disabled={busyLesson === l.lesson_id}
                        className="text-[11px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-stone-300 rounded-full px-3 py-1"
                        data-testid={`learn-complete-${l.lesson_id}`}
                      >
                        {busyLesson === l.lesson_id ? "…" : "Mark done"}
                      </button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
